import { AppPaths } from "@p0security/shared/routes/constants";
import { monitorPriorities } from "@p0security/shared/types/assessment/monitor";
import { Skeleton, Table, TableColumnsType, Typography } from "antd";
import { LinkWithEnvironment } from "components/Assessment/components/LinkWithEnvironment";
import { MonitorPriority } from "components/Assessment/components/monitor/MonitorSeverity";
import {
  FindingsContext,
  MonitorWithCount,
  MonitorWithMetadata,
} from "components/Assessment/contexts/FindingsContext";
import { pickBy } from "lodash";
import { useCallback, useContext } from "react";

import { PanelErrorBoundary } from "./common/PanelErrorBoundary";

export const TopFindings: React.FC<{ isLoading: boolean }> = ({
  isLoading,
}) => {
  const { allMonitors, findingParams } = useContext(FindingsContext);

  const compareMonitors = (one: MonitorWithCount, two: MonitorWithCount) => {
    const oneIndex = monitorPriorities.indexOf(one.priority);
    const twoIndex = monitorPriorities.indexOf(two.priority);
    if (oneIndex !== twoIndex) {
      return oneIndex - twoIndex;
    }
    if (one.count > two.count) return -1;
    if (one.count < two.count) return 1;
    return 0;
  };
  const monitorPath = useCallback(
    (item: MonitorWithMetadata) =>
      `../${AppPaths.Posture}/monitors/${item.monitorId}?${findingParams}`,
    [findingParams]
  );

  const filteredMonitors = pickBy(allMonitors, (monitor) => monitor.count > 0);
  const prioritizedMonitors = Object.values(filteredMonitors).sort(
    (monitorA, monitorB) => compareMonitors(monitorA, monitorB)
  );
  const topFindings = prioritizedMonitors.slice(0, 4);

  const columns: TableColumnsType<MonitorWithCount> = [
    {
      title: "Monitor",
      dataIndex: "label",
      key: "label",
      render: (label, monitor) => (
        <Typography.Text ellipsis>
          <LinkWithEnvironment to={monitorPath(monitor)}>
            {label}
          </LinkWithEnvironment>
        </Typography.Text>
      ),
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      render: (_, monitor: MonitorWithCount) => (
        <MonitorPriority monitor={monitor} />
      ),
    },
    {
      title: "Count",
      dataIndex: "count",
      key: "count",
      render: (count: number) => {
        return <span>{count} findings</span>;
      },
    },
  ];
  return isLoading ? (
    <Skeleton active />
  ) : (
    <div style={{ display: "flex", width: "100%" }}>
      <PanelErrorBoundary title="Top Findings">
        <Table
          columns={columns}
          dataSource={topFindings}
          pagination={false}
          rowKey="label"
        />
      </PanelErrorBoundary>
    </div>
  );
};
